import earcut from 'earcut';
import polygonClipping, { type Pair, type Polygon as ClipPolygon } from 'polygon-clipping';
import type { Vec2 } from './vec.js';
import {
  ensureCounterClockwise,
  pointInOrOnPolygon,
  pointInPolygon,
  pointOnPolygonBoundary,
  polygonArea,
} from './polygon.js';

/** Outer boundary with optional holes; the result of combining several polygons. */
export interface Region {
  readonly outer: readonly Vec2[];
  readonly holes: readonly (readonly Vec2[])[];
}

const toRing = (poly: readonly Vec2[]): Pair[] => {
  const ring = poly.map((p) => [p.x, p.y] as Pair);
  const first = ring[0];
  return first ? [...ring, first] : ring;
};
// polygon-clipping repeats the first vertex at the end of every ring.
const fromRing = (ring: readonly Pair[]): Vec2[] => ring.slice(0, -1).map(([x, y]) => ({ x, y }));

/**
 * Union of simple polygons (e.g. adjacent land parcels) as separate regions. Parcels sharing
 * an edge merge into one region; the shared edge disappears.
 */
export function unionPolygons(polys: readonly (readonly Vec2[])[]): Region[] {
  const valid = polys.filter((p) => p.length >= 3);
  if (valid.length === 0) return [];
  const [first, ...rest] = valid.map((p): ClipPolygon => [toRing(p)]);
  if (!first) return [];
  const result = rest.length === 0 ? [first] : polygonClipping.union(first, ...rest);
  return result.map(([outer, ...holes]) => ({
    outer: ensureCounterClockwise(fromRing(outer ?? [])),
    holes: holes.map(fromRing),
  }));
}

const toRegions = (result: readonly ClipPolygon[]): Region[] =>
  result.map(([outer, ...holes]) => ({
    outer: ensureCounterClockwise(fromRing(outer ?? [])),
    holes: holes.map(fromRing),
  }));

/** `poly` pushed out by `d` from its centroid and shifted a hair, as a retry for the clipper. */
const nudged = (poly: readonly Vec2[], d: number): ClipPolygon => {
  const c = poly.reduce((s, p) => ({ x: s.x + p.x / poly.length, y: s.y + p.y / poly.length }), {
    x: 0,
    y: 0,
  });
  return [
    toRing(
      poly.map((p) => {
        const l = Math.hypot(p.x - c.x, p.y - c.y) || 1;
        return {
          x: p.x + ((p.x - c.x) / l) * d + d * 0.37,
          y: p.y + ((p.y - c.y) / l) * d + d * 0.61,
        };
      }),
    ),
  ];
};

/**
 * `unionPolygons` for many heavily overlapping polygons (e.g. a vehicle's positions along a
 * path), where polygon-clipping sometimes gives up: then merged a few at a time, a polygon it
 * still trips over retried slightly grown and shifted (by millimetres), and left out only if
 * that fails too.
 */
export function unionPolygonsRobust(polys: readonly (readonly Vec2[])[]): Region[] {
  const valid = polys.filter((p) => p.length >= 3);
  try {
    return unionPolygons(valid);
  } catch {
    // Merged step by step below.
  }
  let acc: ClipPolygon[] = [];
  const merge = (more: ClipPolygon[]): boolean => {
    try {
      if (acc.length > 0) acc = polygonClipping.union(acc, ...more);
      else {
        const [head, ...rest] = more;
        if (head) acc = rest.length > 0 ? polygonClipping.union(head, ...rest) : [head];
      }
      return true;
    } catch {
      return false;
    }
  };
  const CHUNK = 8;
  for (let i = 0; i < valid.length; i += CHUNK) {
    const chunk = valid.slice(i, i + CHUNK);
    if (merge(chunk.map((p): ClipPolygon => [toRing(p)]))) continue;
    for (const p of chunk) {
      if (merge([[toRing(p)]])) continue;
      for (const d of [0.001, 0.003, 0.01]) if (merge([nudged(p, d)])) break;
    }
  }
  return toRegions(acc);
}

/** On the millimetre grid: nearly coinciding edges then coincide exactly. */
const snapped = (poly: readonly Vec2[]): Vec2[] =>
  poly.map((p) => ({ x: Math.round(p.x * 1000) / 1000, y: Math.round(p.y * 1000) / 1000 }));

/** The first of `tries` the clipper manages, or `undefined` if it gives up on all of them. */
function firstThatWorks(tries: readonly (() => ClipPolygon[])[]): ClipPolygon[] | undefined {
  for (const attempt of tries) {
    try {
      return attempt();
    } catch {
      // polygon-clipping gives up on nearly coinciding edges; the next try differs a hair.
    }
  }
  return undefined;
}

/**
 * `subject` minus all `cutters`; the result may fall apart into several regions or get holes.
 * Where the clipper trips over nearly coinciding edges (a dock pit right on the parcel
 * boundary): once more on the millimetre grid, then cutter by cutter, a cutter it still trips
 * over grown by millimetres, and left out only if that fails too. Never throws.
 */
export function subtractPolygons(
  subject: readonly Vec2[],
  cutters: readonly (readonly Vec2[])[],
): Region[] {
  if (subject.length < 3) return [];
  const valid = cutters.filter((p) => p.length >= 3);
  if (valid.length === 0) return toRegions([[toRing(subject)]]);
  const whole = firstThatWorks([
    () =>
      polygonClipping.difference([toRing(subject)], ...valid.map((p): ClipPolygon => [toRing(p)])),
    () =>
      polygonClipping.difference(
        [toRing(snapped(subject))],
        ...valid.map((p): ClipPolygon => [toRing(snapped(p))]),
      ),
  ]);
  if (whole) return toRegions(whole);
  let acc: ClipPolygon[] = [[toRing(subject)]];
  for (const p of valid) {
    const cuts: ClipPolygon[] = [
      [toRing(p)],
      [toRing(snapped(p))],
      ...[0.001, 0.003, 0.01].map((d) => nudged(p, d)),
    ];
    acc = firstThatWorks(cuts.map((cut) => () => polygonClipping.difference(acc, cut))) ?? acc;
  }
  return toRegions(acc);
}

/**
 * The parts of `a` that also lie in `b`; retried on the millimetre grid and with `b` grown by
 * millimetres, nothing if the clipper still gives up. Never throws.
 */
export function intersectPolygons(a: readonly Vec2[], b: readonly Vec2[]): Region[] {
  if (a.length < 3 || b.length < 3) return [];
  const result = firstThatWorks([
    () => polygonClipping.intersection([toRing(a)], [toRing(b)]),
    () => polygonClipping.intersection([toRing(snapped(a))], [toRing(snapped(b))]),
    ...[0.001, 0.003].map((d) => () => polygonClipping.intersection([toRing(a)], nudged(b, d))),
  ]);
  return toRegions(result ?? []);
}

/**
 * Triangles of a region with any number of holes (earcut): its points (outer ring, then the
 * holes) and index triples into them, each triangle counter-clockwise.
 */
export function triangulateRegion(r: Region): { points: Vec2[]; triangles: number[] } {
  const outer = ensureCounterClockwise(r.outer);
  const holes = r.holes.map((h) => ensureCounterClockwise(h).reverse());
  const points = [outer, ...holes].flat();
  const holeStarts: number[] = [];
  let start = outer.length;
  for (const h of holes) {
    holeStarts.push(start);
    start += h.length;
  }
  const raw = earcut(
    points.flatMap((p) => [p.x, p.y]),
    holeStarts,
  );
  const triangles: number[] = [];
  for (let i = 0; i + 2 < raw.length; i += 3) {
    const [a, b, c] = [raw[i], raw[i + 1], raw[i + 2]] as [number, number, number];
    const pa = points[a] as Vec2;
    const pb = points[b] as Vec2;
    const pc = points[c] as Vec2;
    const ccw = (pb.x - pa.x) * (pc.y - pa.y) - (pb.y - pa.y) * (pc.x - pa.x) > 0;
    triangles.push(...(ccw ? [a, b, c] : [a, c, b]));
  }
  return { points, triangles };
}

export const regionArea = (r: Region): number =>
  polygonArea(r.outer) - r.holes.reduce((s, h) => s + polygonArea(h), 0);

export function pointInRegion(p: Vec2, r: Region, tolerance: number): boolean {
  if (!pointInOrOnPolygon(p, r.outer, tolerance)) return false;
  // Points on a hole boundary still belong to the region.
  return r.holes.every((h) => !pointInPolygon(p, h) || pointOnPolygonBoundary(p, h, tolerance));
}

/** Total area where parcels overlap (should be 0 for a clean cadastral layout). */
export function overlapArea(polys: readonly (readonly Vec2[])[]): number {
  const sum = polys.reduce((s, p) => s + polygonArea(p), 0);
  return Math.max(0, sum - unionPolygons(polys).reduce((s, r) => s + regionArea(r), 0));
}
